import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Settings,
  Mail,
  DollarSign,
  FileText,
  FileCheck,
  Menu,
  X,
  ChevronRight
} from 'lucide-react';
import { UserProfile } from '@/shared/types';

interface AdminSidebarProps {
  activePath: string;
  onNavigate: (path: string) => void;
  userProfile: UserProfile | null;
  onLogout: () => Promise<void>;
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({
  activePath,
  onNavigate,
  userProfile,
  onLogout
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const config = userProfile ? { appName: 'SwaySuite', platformLogoUrl: userProfile.logoUrl } : { appName: 'SwaySuite', platformLogoUrl: null };

  const menuItems = [
    {
      path: '/admin/dashboard',
      label: 'Dashboard',
      icon: LayoutDashboard,
    },
    {
      path: '/admin/users',
      label: 'Users',
      icon: Users,
    },
    {
      path: '/admin/config',
      label: 'Settings',
      icon: Settings,
    },
    {
      path: '/admin/email',
      label: 'Email',
      icon: Mail,
    },
    {
      path: '/admin/finance',
      label: 'Finance',
      icon: DollarSign,
    },
    {
      path: '/admin/audit',
      label: 'Audit Logs',
      icon: FileText,
    },
    {
      path: '/admin/templates',
      label: 'Templates',
      icon: FileCheck,
    }
  ];

  const isActive = (path: string) => {
    if (path === '/admin/dashboard') {
      return activePath === '/admin' || activePath === '/admin/dashboard';
    }
    return activePath === path;
  };

  return (
    <>
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 rounded bg-white shadow border border-gray-200 hover:bg-gray-50 transition-all"
        >
          {isMobileMenuOpen ? <X size={18} className="text-gray-700" /> : <Menu size={18} className="text-gray-700" />}
        </button>
      </div>

      {/* Sidebar */}
      <div className={`
        fixed lg:relative inset-y-0 left-0 z-40 w-64 bg-white border-r border-gray-200
        transform transition-transform duration-300 ease-in-out
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="h-16 px-5 flex items-center border-b border-gray-200">
            <div className="flex items-center gap-3">
              {config.platformLogoUrl ? (
                <img
                  src={config.platformLogoUrl}
                  alt={config.appName}
                  className="h-8 w-auto object-contain"
                />
              ) : (
                <div className="w-8 h-8 bg-gray-900 rounded flex items-center justify-center">
                  <span className="text-white font-semibold text-base">{config.appName.charAt(0)}</span>
                </div>
              )}
              <div>
                <h1 className="text-base font-semibold text-gray-900">{config.appName}</h1>
                <p className="text-xs text-gray-500">Admin Portal</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);

              return (
                <button
                  key={item.path}
                  onClick={() => {
                    onNavigate(item.path);
                    setIsMobileMenuOpen(false);
                  }}
                  className={`
                    w-full flex items-center gap-3 px-3 py-2 rounded text-left transition-all duration-150 group
                    ${active
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                    }
                  `}
                >
                  <Icon size={16} className={active ? 'text-white' : 'text-gray-400 group-hover:text-gray-600 transition-colors'} />
                  <span className="text-sm font-medium">{item.label}</span>
                  {active && (
                    <ChevronRight size={14} className="ml-auto text-white/60" />
                  )}
                </button>
              );
            })}
          </nav>

        </div>
      </div>

      {/* Mobile overlay */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/20 z-30 transition-opacity"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
    </>
  );
};

export { AdminSidebar };
